import { useId, useState } from 'react'

import { cn, numberToCurrency } from '@/utils'
import type { SeriesPoint } from '../model/portfolioOverview'
import type { MonthKey } from '../model/types'
import { longDate } from '../portfolioText'
import { chartGaps, gapNote } from './chartGaps'
import {
  buildChartGeometry,
  CHART_WIDTH,
  type ChartGeometry,
  GAP_LABEL_Y,
  PLOT_BOTTOM,
  PLOT_TOP,
  X_LABEL_Y,
} from './chartGeometry'
import { noTotalNotes } from './heroText'

const TICK_LABEL_OFFSET = 8
const TICK_TEXT_BASELINE = 4
const POINT_RADIUS = 3.5
const AS_OF_POINT_RADIUS = 5

interface TotalOverTimeChartProps {
  series: SeriesPoint[]
  asOfMonth: MonthKey
  label: string
}

const Gridlines = ({ geometry }: { geometry: ChartGeometry }) =>
  geometry.ticks.map((tick) => (
    <g key={tick.text}>
      <line
        x1={geometry.plotLeft}
        x2={geometry.plotRight}
        y1={tick.y}
        y2={tick.y}
        className="stroke-border"
        strokeWidth={1}
      />
      <text
        x={geometry.plotLeft - TICK_LABEL_OFFSET}
        y={tick.y + TICK_TEXT_BASELINE}
        textAnchor="end"
        className="fill-ink-subtle text-[11px]"
      >
        {tick.text}
      </text>
    </g>
  ))

const Labels = ({
  labels,
  y,
}: {
  labels: ChartGeometry['xLabels']
  y: number
}) =>
  labels.map((label) => (
    <text
      key={`${label.x}-${label.text}`}
      x={label.x}
      y={y}
      textAnchor={label.anchor}
      className={cn(
        'text-[11px]',
        label.strong ? 'fill-blue-7 font-medium' : 'fill-ink-subtle',
      )}
    >
      {label.text}
    </text>
  ))

const Points = ({ geometry }: { geometry: ChartGeometry }) =>
  geometry.points.map((point) =>
    point.y == null ? null : (
      <circle
        key={point.month}
        cx={point.x}
        cy={point.y}
        r={point.isAsOf ? AS_OF_POINT_RADIUS : POINT_RADIUS}
        className={cn(
          'stroke-white',
          point.isAsOf ? 'fill-blue-7' : 'fill-blue-6',
        )}
        strokeWidth={2}
      />
    ),
  )

const PointTip = ({ point, x }: { point: SeriesPoint; x: number }) => (
  <div
    className="pointer-events-none absolute top-6 z-10 w-[230px] -translate-x-1/2 rounded-md border bg-white px-2.5 py-2 text-xs shadow-md"
    style={{ left: `${(x / CHART_WIDTH) * 100}%` }}
  >
    <b className="block font-medium">{longDate(point.asOfDate)}</b>
    {point.total == null ? (
      <span className="text-ink-subtle">
        No total. {noTotalNotes(point)[0] ?? ''}
      </span>
    ) : (
      <span className="tabular-nums text-ink-subtle">
        {numberToCurrency(point.total)}
      </span>
    )}
  </div>
)

// The total at each statement month. A month with a missing statement is hatched and has no point,
// so the line breaks there instead of being filled from a later statement.
export const TotalOverTimeChart = ({
  series,
  asOfMonth,
  label,
}: TotalOverTimeChartProps) => {
  const hatchId = `hatch-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const geometry = buildChartGeometry(series, asOfMonth)
  const asOfPoint = geometry.points.find((point) => point.isAsOf)
  const columnWidth =
    (geometry.plotRight - geometry.plotLeft) / Math.max(series.length - 1, 1)
  const note = gapNote(chartGaps(series))

  return (
    <div className="relative mt-6">
      <div className="mb-1.5 text-xs text-ink-subtle">{label}</div>
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${geometry.height}`}
        role="img"
        aria-label={label}
        className="block w-full overflow-visible"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <defs>
          <pattern
            id={hatchId}
            width={6}
            height={6}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width={6} height={6} className="fill-blue-1" />
            <line
              x1={0}
              y1={0}
              x2={0}
              y2={6}
              className="stroke-border"
              strokeWidth={2}
            />
          </pattern>
        </defs>
        <Gridlines geometry={geometry} />
        {geometry.gaps.map((gap) => (
          <rect
            key={gap.key}
            x={gap.x}
            y={PLOT_TOP}
            width={gap.width}
            height={PLOT_BOTTOM - PLOT_TOP}
            fill={`url(#${hatchId})`}
          />
        ))}
        {asOfPoint && asOfPoint.y == null && (
          <line
            x1={asOfPoint.x}
            x2={asOfPoint.x}
            y1={PLOT_TOP}
            y2={PLOT_BOTTOM}
            className="stroke-blue-6"
            strokeWidth={1.5}
            strokeDasharray="3 3"
          />
        )}
        {geometry.lines.map((line) => (
          <polyline
            key={line}
            points={line}
            fill="none"
            className="stroke-blue-6"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        <Points geometry={geometry} />
        <Labels labels={geometry.xLabels} y={X_LABEL_Y} />
        <Labels labels={geometry.gapLabels} y={GAP_LABEL_Y} />
        {geometry.points.map((point, index) => (
          <rect
            key={point.month}
            x={point.x - columnWidth / 2}
            y={PLOT_TOP}
            width={columnWidth}
            height={PLOT_BOTTOM - PLOT_TOP}
            fill="transparent"
            onMouseEnter={() => setHoveredIndex(index)}
          />
        ))}
      </svg>
      {hoveredIndex != null && (
        <PointTip
          point={series[hoveredIndex]}
          x={geometry.points[hoveredIndex].x}
        />
      )}
      {note && (
        <p className="mt-2 max-w-[560px] text-xs text-ink-subtle">{note}</p>
      )}
    </div>
  )
}
